import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
const DAY_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'prof_espagnol_pret_countdown_target';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + DAY_MS;
  const storedTarget = window.localStorage.getItem(STORAGE_KEY);
  if (storedTarget) {
    const parsedTarget = Number.parseInt(storedTarget, 10);
    if (!Number.isNaN(parsedTarget) && parsedTarget > Date.now()) return parsedTarget;
  }
  const target = Date.now() + DAY_MS;
  try {
    window.localStorage.setItem(STORAGE_KEY, target.toString());
  } catch {
    /* storage unavailable */
  }
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(DAY_MS);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + DAY_MS;
        try {
          window.localStorage.setItem(STORAGE_KEY, nextTarget.toString());
        } catch {
          /* storage unavailable */
        }
        setTarget(nextTarget);
        setRemaining(DAY_MS);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const progress = Math.min(90, 65 + (DAY_MS - remaining) / DAY_MS * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-gradient-to-b from-[#2a0808] to-[#1a0404] border border-red-500/40 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(239,68,68,0.15)]" data-id="element-32" data-mp="2dopmvd26rdsp">
      <div className="text-center" data-id="element-33" data-mp="2nbjup94u8xcz">
        <div className="flex items-center justify-center gap-2 text-red-400 font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-34" data-mp="2jbjhfrth6s7i">
          <AlarmClock size={20} className="animate-pulse" data-id="element-35" data-mp="2ecgag1txqbww" />
          <span data-id="element-36" data-mp="29eewfvbd92sa">Le prix de lancement se termine dans :</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-37" data-mp="24n560shztvfc">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-38" data-mp="24l269gr1gnxf" />
          <span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-39" data-mp="1znai8xtillyi">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-40" data-mp="1vmqjxqx7gcas" />
          <span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-41" data-mp="1vobf9ircfz9p">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-42" data-mp="1qm2j6dj6ch3d" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-gray-200 mb-8" data-id="element-43" data-mp="1lnr8k0l222k3">
          Après ça, le prix augmente et il ne redescendra pas.
        </p>
        <div className="space-y-2" data-id="element-44" data-mp="18me3m4a1cdl">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-45" data-mp="64jx606xcwu2">
            <span className="text-red-300" data-id="element-46" data-mp="b5hde78djgsx">Places au prix de lancement</span>
            <span className="text-red-400" data-id="element-47" data-mp="f5rlbpfq8haf">Il en reste peu</span>
          </div>
          <div className="w-full h-2.5 bg-red-950/60 rounded-full overflow-hidden border border-red-500/30" data-id="element-48" data-mp="3el4xkslhqhlk">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.6)]" data-id="element-49" data-mp="3jjzqwoh33ojx" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-red-300 font-medium" data-id="element-50" data-mp="2vskl2lq3mp3x">{Math.round(progress)}% des places occupées</p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-black border-2 border-red-500/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px] shadow-[inset_0_0_15px_rgba(239,68,68,0.1)]" data-id="element-51" data-mp="2btlljdqt51uo">
      <div className="flex items-baseline justify-center gap-1" data-id="element-52" data-mp="2lr01lc8tjomg">
        <span className="text-4xl md:text-5xl font-bold text-white tabular-nums" data-id="element-53" data-mp="2lonb2aozw5xs">{value}</span>
        <span className="text-xs md:text-sm font-medium text-red-300" data-id="element-54" data-mp="2zgac0ntvu2mw">{label}</span>
      </div>
    </div>;
}